export type BanxaPaymentMethod = string;

export type BanxaRequest = {
  fiat: string;
  crypto: string;
  blockchain: string;
  paymentMethodId: BanxaPaymentMethod;
  fiatAmount: string;
};

export type BanxaSellRequest = Omit<BanxaRequest, "fiatAmount"> & {
  cryptoAmount: string;
};

export type BanxaBlockchain = {
  id: string;
  description?: string;
  isDefaultBlockchain?: boolean;
  minimum?: string;
};

export type BanxaCryptocurrency = {
  id: string;
  description?: string;
  blockchains: BanxaBlockchain[];
};

export type BanxaFiatPaymentMethod = {
  id: string;
  name?: string;
  minimum?: string;
  maximum?: string;
};

export type BanxaFiat = {
  id: string;
  description?: string;
  symbol?: string;
  supportedPaymentMethods: BanxaFiatPaymentMethod[];
};

export type BanxaSellCapabilities = {
  cryptocurrencies: BanxaCryptocurrency[];
  fiats: BanxaFiat[];
};

export type BanxaBuyCapabilities = {
  cryptocurrencies: BanxaCryptocurrency[];
  fiats: BanxaFiat[];
};

export type BanxaQuote = {
  paymentMethodId: string;
  cryptoAmount: string;
  fiatAmount: string;
  processingFee: string;
  networkFee: string;
  partnerFee?: string;
};

export const banxaFeeAmount = (amount?: string) => {
  const value = Number(amount);
  return Number.isFinite(value) ? value : 0;
};

export const banxaFeeTotal = (quote?: BanxaQuote) =>
  banxaFeeAmount(quote?.processingFee) +
  banxaFeeAmount(quote?.networkFee) +
  banxaFeeAmount(quote?.partnerFee);

export type BanxaOrder = {
  checkoutUrl: string;
  id: string;
  externalOrderId?: string | null;
  externalCustomerId: string;
  fiat: string;
  fiatAmount: string;
  crypto: string;
  cryptoAmount?: string | null;
  blockchain: string;
};

type BanxaError = {
  error?: string;
  message?: string;
  fiat?: string;
  minimumFiatAmount?: string;
};

export class BanxaMinimumAmountError extends Error {
  fiat: string;
  minimumFiatAmount: string;

  constructor(fiat: string, minimumFiatAmount: string) {
    super(`Minimum purchase amount is ${fiat} ${minimumFiatAmount}.`);
    this.name = "BanxaMinimumAmountError";
    this.fiat = fiat;
    this.minimumFiatAmount = minimumFiatAmount;
  }
}

const request = async <T>(body: object): Promise<T> => {
  const response = await fetch("/api/banxa", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let data: T | BanxaError;

  try {
    data = text ? (JSON.parse(text) as T | BanxaError) : {};
  } catch {
    throw new Error("Banxa returned an unreadable response. Please try again.");
  }

  if (!response.ok) {
    const error = data as BanxaError;
    if (error.fiat && error.minimumFiatAmount) {
      throw new BanxaMinimumAmountError(error.fiat, error.minimumFiatAmount);
    }
    throw new Error(
      error.error || error.message || "Banxa is unavailable. Please try again."
    );
  }

  return data as T;
};

export const getBanxaQuote = async (
  input: BanxaRequest
): Promise<BanxaQuote> => {
  const result = await request<BanxaQuote | BanxaQuote[]>({
    action: "quote",
    ...input,
  });
  const quote = Array.isArray(result) ? result[0] : result;
  if (!quote) throw new Error("Banxa did not return a quote.");
  return quote;
};

export const createBanxaOrder = (
  input: BanxaRequest & {
    walletAddress: string;
    customerId: string;
  }
) =>
  request<BanxaOrder>({
    action: "buy",
    ...input,
  });

const record = (input: unknown): Record<string, unknown> | undefined =>
  typeof input === "object" && input !== null
    ? (input as Record<string, unknown>)
    : undefined;

const text = (input: unknown) =>
  typeof input === "string" && input.trim() ? input.trim() : undefined;

const uniqueById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter(({ id }) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const normalizeCryptocurrencies = (input: unknown): BanxaCryptocurrency[] =>
  (Array.isArray(input) ? input : []).flatMap((entry) => {
    const value = record(entry);
    const id = text(value?.id);
    if (!id) return [];
    const blockchains = (
      Array.isArray(value?.blockchains) ? value.blockchains : []
    ).flatMap((blockchain) => {
      const item = record(blockchain);
      const blockchainId = text(item?.id);
      if (!blockchainId) return [];
      return [
        {
          id: blockchainId,
          description: text(item?.description),
          isDefaultBlockchain:
            typeof item?.isDefaultBlockchain === "boolean"
              ? item.isDefaultBlockchain
              : undefined,
          minimum: text(item?.minimum),
        },
      ];
    });
    return [{ id, description: text(value?.description), blockchains }];
  });

const normalizeFiats = (input: unknown): BanxaFiat[] =>
  (Array.isArray(input) ? input : []).flatMap((entry) => {
    const value = record(entry);
    const id = text(value?.id);
    if (!id) return [];
    const supportedPaymentMethods = uniqueById(
      (Array.isArray(value?.supportedPaymentMethods)
        ? value.supportedPaymentMethods
        : []
      ).flatMap((paymentMethod) => {
        const item = record(paymentMethod);
        const paymentMethodId = text(item?.id);
        if (!paymentMethodId) return [];
        return [
          {
            id: paymentMethodId,
            name: text(item?.name),
            minimum: text(item?.minimum),
            maximum: text(item?.maximum),
          },
        ];
      })
    );
    return [
      {
        id,
        description: text(value?.description),
        symbol: text(value?.symbol),
        supportedPaymentMethods,
      },
    ];
  });

let buyCapabilitiesRequest: Promise<BanxaBuyCapabilities> | undefined;

export const getBanxaBuyCapabilities = () => {
  if (!buyCapabilitiesRequest) {
    buyCapabilitiesRequest = request<{
      cryptocurrencies?: unknown;
      fiats?: unknown;
    }>({ action: "buyCapabilities" })
      .then((result) => ({
        cryptocurrencies: normalizeCryptocurrencies(result.cryptocurrencies),
        fiats: normalizeFiats(result.fiats),
      }))
      .catch((error: unknown) => {
        buyCapabilitiesRequest = undefined;
        throw error;
      });
  }
  return buyCapabilitiesRequest;
};

let sellCapabilitiesRequest: Promise<BanxaSellCapabilities> | undefined;

export const getBanxaSellCapabilities = () => {
  if (!sellCapabilitiesRequest) {
    sellCapabilitiesRequest = request<{
      cryptocurrencies?: unknown;
      fiats?: unknown;
    }>({ action: "sellCapabilities" })
      .then((result) => ({
        cryptocurrencies: normalizeCryptocurrencies(result.cryptocurrencies),
        fiats: normalizeFiats(result.fiats),
      }))
      .catch((error: unknown) => {
        sellCapabilitiesRequest = undefined;
        throw error;
      });
  }
  return sellCapabilitiesRequest;
};

export const getBanxaSellQuote = async (
  input: BanxaSellRequest
): Promise<BanxaQuote> => {
  const result = await request<BanxaQuote | BanxaQuote[]>({
    action: "sellQuote",
    ...input,
  });
  const quote = Array.isArray(result) ? result[0] : result;
  if (!quote) throw new Error("Banxa did not return a quote.");
  return quote;
};

export const createBanxaSellOrder = (
  input: BanxaSellRequest & {
    walletAddress: string;
    customerId: string;
  }
) =>
  request<BanxaOrder>({
    action: "sell",
    ...input,
  });
